import React from "react";
import Immutable, { Map, fromJS } from "immutable";
import onecolor from "onecolor";
import _ from "lodash";

import PropertyMaps from "../constants/PropertyMaps";
import SystemFonts from "../constants/SystemFonts";
import AppConstants from "../constants/AppConstants";
import { gatherProps } from "./Merge/MergeUtils.mjs";
import {
  BUTTON_BORDER_COLOR,
  BUTTON_BORDER_WIDTH,
  BUTTON_BORDER_STYLE,
  BUTTON_BORDER_RADIUS,
  TEXT_COLOR,
  TEXT_HOVER_COLOR,
  OPACITY,
  BACKDROP_FILTER,
} from "../constants/MergeConstants";
const { BORDER_CIRCLE_RAD } = AppConstants;
import { LAYOUT_DESKTOP } from "@/constants/DesignConstants.mjs";
import {
  BACKDROP_FILTER_CONFIGS
} from "@/constants/AdvancedFillModePresetConstants";
import { hasBackdropFilter, getAdvanceFilterValueFromArray } from "./Element/FilterUtils";

const BUTTON_STYLE_RESETS = fromJS({ [BUTTON_BORDER_WIDTH]: 0 });

/**
 * Converts button styles to text styles by extracting text-related properties
 * and transforming them into a format suitable for text styling.
 *
 * @param {Immutable.Map} buttonStyles - Map of button styles to convert
 * @returns {Immutable.Map} New map containing the converted text styles
 */
export const convertButtonStylesToTextStyles = (buttonStyles) => {
  let newButtonStyles = buttonStyles;
  buttonStyles.forEach((buttonStyle, key) => {
    let textStyle = buttonStyle.set("desktop", buttonStyle.getIn(["desktop", "text"]));
    textStyle = textStyle.set("mobile", buttonStyle.getIn(["mobile", "text"]));
    textStyle = textStyle.set(
      "sync",
      buttonStyle
        .get("sync")
        .filter((prop) => prop.includes("."))
        .map((prop) => prop.replace("text.", ""))
    );
    newButtonStyles = newButtonStyles.set(key, textStyle);
  });
  return newButtonStyles;
};

/**
 * This is used to create style css
 *
 */
class StyleUtils {
  constructor() {
    this.styleClass = _.memoize((name) => `si-style-${name.toLowerCase().replace(/\s+/g, "-")}`);
  }

  gatherLinkDefaults(linkProps, color) {
    const linkColor = linkProps.get(TEXT_COLOR, color);
    const hoverColor = linkProps.get(TEXT_HOVER_COLOR, `${linkColor}:80`);

    return Map({
      [TEXT_COLOR]: linkColor,
      [TEXT_HOVER_COLOR]: hoverColor,
    });
  }

  /**
   *
   * @param Immutable.Map theme
   * @param layout
   * @returns {string}
   */
  getThemeCSS(theme, layout) {
    let css = "";

    const buttonStyles = theme.get("styles").filter((style) => style.get("type") === "button");
    const buttonTextStyles = convertButtonStylesToTextStyles(buttonStyles);

    const textStyles = theme
      .get("styles")
      .filter((style) => style.get("type") === "text")
      .merge(buttonTextStyles);

    textStyles.forEach((style) => {
      const layoutStyle = style
        .get(layout)
        .merge(gatherProps(style, layout, style.get("sync", [])));

      let cssStyle = this.getStyle(PropertyMaps.STYLE_PROPS_THEME, layoutStyle, theme, "css", null);

      css += "." + this.getStyleClass(style.get("id")) + " " + this.getStyleString(cssStyle);

      let linkStyle = Immutable.fromJS(PropertyMaps.DEFAULT_LINK_STYLE);

      linkStyle = linkStyle.set(
        PropertyMaps.PROP_TEXT_COLOR,
        layoutStyle.get(PropertyMaps.PROP_TEXT_COLOR)
      );

      let styleLinkStyle = layoutStyle.get("link", false);
      if (styleLinkStyle) {
        linkStyle = linkStyle.merge(styleLinkStyle);
      }

      let linkCSSStyle = this.getStyle(PropertyMaps.STYLE_PROPS_LINK, linkStyle, theme, "css");
      css += "." + this.getStyleClass(style.get("id")) + " a " + this.getStyleString(linkCSSStyle);
    });
    // Iterate through each button style in the theme and generate CSS for both the button container
    buttonStyles?.forEach((buttonStyle, buttonType) => {
      const layoutStyle = BUTTON_STYLE_RESETS.merge(
        buttonStyle.get(layout).filter((prop, key) => key !== PropertyMaps.PROP_DROP_SHADOW)
      ).merge(gatherProps(buttonStyle, layout, buttonStyle.get("sync", []), true));

      const buttonCSSStyle = this.getStyle(
        PropertyMaps.STYLE_PROPS_BUTTON,
        layoutStyle,
        theme,
        "css"
      );

      const buttonSelector = `.${this.getStyleClass(`button-${buttonType}`)}`;
      const elementSelector = `.si-element:has(${buttonSelector})`;

      const setButtonElementStyles = (selector, data) => {
        const shadowStyle = data.get(PropertyMaps.PROP_DROP_SHADOW);
        const borderStyle = data.get("border");
        // Need to just have border-radius on element, other border styles should be on button
        const shadowCSS = this.getShadowStyle(data, theme, "button");
        const borderCSS = this.getBorderStyle(data, theme);

        const styles = {};

        if (data.get(OPACITY) !== undefined) {
          styles.opacity = data.get(OPACITY) / 100;
        }

        if (shadowStyle || borderStyle) {
          Object.assign(styles, shadowCSS);
          if (borderStyle) {
            styles["border-radius"] = borderCSS["border-radius"] || "inherit";
          }
        }

        if (Object.keys(styles).length) {
          css += `${selector} ${this.getStyleString(styles)}`;
        }
      };

      setButtonElementStyles(elementSelector, layoutStyle);

      // hover stuff 🥲
      if (layout === LAYOUT_DESKTOP) {
        const hoverLayoutData = layoutStyle.get("hover", Map());
        const elementHoverSelector = `.si-element.hover:has(${buttonSelector})`;

        setButtonElementStyles(elementHoverSelector, hoverLayoutData);
      }

      css += `${buttonSelector} ${this.getStyleString({
        ...buttonCSSStyle,
        ...this.getBorderStyle(layoutStyle, theme),
      })}`;
    });
    return css;
  }

  /**
   * The format property determines how the styles are named.  This is use for properties
   * that may be set internally and are not part of the property map
   *
   * If set to 'react' they are returned camelCase to be used on a react component
   * If set to 'css' they are return snake-case to be used in css
   *
   * @param styleProps
   * @param propContainer
   * @param theme
   * @param format css | react  'react' is the default and keys will be return in camelCase
   * @returns {{}} An object containing key value pairs.  The key is format is based on the format value passed in
   */
  getStyle(
    /** Array **/ styleProps,
    /** Immutable.Map **/ propContainer,
    /** Immutable.Map **/ theme,
    /** String **/ format = "react",
    /** Object **/ propOverrides = null
  ) {
    var style = {};
    if (!propContainer) {
      return style;
    }

    var formatPropMap =
      format == "css" ? PropertyMaps.SHOWIT_TO_CSS_PROP_MAP : PropertyMaps.SHOWIT_TO_REACT_PROP_MAP;

    _.forEach(
      styleProps,
      _.bind(function (propName) {
        let val = propContainer.get(propName);

        if (propOverrides) {
          propName = _.get(propOverrides, propName, propName);
        }

        let formatPropName = formatPropMap[propName];

        const isParentProp = PropertyMaps.IS_PARENT_PROP[propName];

        if (
          val !== undefined &&
          propName !== "border" &&
          propName !== "shadow" &&
          (formatPropName || isParentProp)
        ) {
          switch (propName) {
            case "fillType":
              if (val === AppConstants.BG_TYPE_COLOR) {
                const bgColor = propContainer.get("bgcolor");
                if (bgColor !== undefined) {
                  style[formatPropMap["bgColor"]] = this.getColorValue(bgColor, theme);
                }
              } else if (val === AppConstants.BG_TYPE_GRADIENT) {
                const gradientStyle = this.getGradientStyle(propContainer, theme);
                if (
                  gradientStyle &&
                  gradientStyle.backgroundImage &&
                  formatPropMap["backgroundImage"]
                ) {
                  style[formatPropMap["backgroundImage"]] = gradientStyle.backgroundImage;
                }
              }
              break;

            case PropertyMaps.PROP_TEXT_COLOR:
            case PropertyMaps.PROP_COLOR:
            case PropertyMaps.PROP_BG_COLOR_V2:
            case PropertyMaps.PROP_BG_COLOR:
            case PropertyMaps.PROP_BORDER_COLOR:
              style[formatPropName] = this.getColorValue(val, theme);
              break;
            case PropertyMaps.PROP_FONT:
              this._applyFontStyle(
                style,
                this._getFontStyle(val, theme, formatPropMap),
                formatPropMap
              );
              break;
            default:
              style[formatPropName] = this._formatValue(propName, val);
          }
        }
      }, this)
    );
    return style;
  }

  getStyleInputOptions(styles, currentValue, modifiedStyle) {
    const inputOptions = [];

    if (!currentValue) {
      inputOptions.push(<option value="">-- Select --</option>);
    }

    if (styles && styles.size > 0) {
      styles.forEach((style) => {
        inputOptions.push(
          <option key={"user" + style.get("id")} value={style.get("id")}>
            {style.get("name")}
          </option>
        );
        if (currentValue == style.get("id") && modifiedStyle) {
          inputOptions.push(
            <option key={"usermod" + style.get("id")} value={style.get("id") + "+"}>
              {style.get("name") + " +"}
            </option>
          );
        }
      });
    }

    return inputOptions;
  }

  _applyFontStyle(style, fontStyle, propMap) {
    if (_.has(fontStyle, propMap["family"])) {
      _.merge(style, fontStyle);
    }

    return style;
  }

  _getFontStyle(fontName, theme, propMap) {
    var style = {};

    style[propMap["family"]] = false;
    style[propMap["weight"]] = 400;
    style[propMap["style"]] = "normal";

    var font = theme.getIn(["fonts", fontName], false);
    if (font) {
      //immutable object from theme
      style[propMap["family"]] = font.get("family", false);
      style[propMap["weight"]] = font.get("weight", "400");
      style[propMap["style"]] = font.get("style", "normal");
    } else {
      font = SystemFonts[fontName]; //system fonts are not in an Immutable Map
      if (font) {
        style[propMap["family"]] = _.get(font, "family", false);
        style[propMap["weight"]] = _.get(font, "weight", "400");
        style[propMap["style"]] = _.get(font, "style", "normal");
      }
    }
    if ("regular" == style[propMap["weight"]]) {
      style[propMap["weight"]] = "400";
    }

    if (_.has(style, propMap["family"]) && style[propMap["family"]] !== false) {
      style[propMap["family"]] = "'" + style[propMap["family"]] + "'";
    }

    return style;
  }

  adjustColorOpacity(val, amount) {
    var parts = val.split(":");
    var color = parts[0];
    var opacity = 100;

    if (parts.length > 1) {
      opacity = parseInt(parts[1]);
    }

    opacity = _.clamp(opacity + amount, 0, 100);

    return opacity == 100 ? color : color + ":" + opacity;
  }

  /**
   * Sets the opacity of the color to the specified amount if no
   * opacity is set.
   * @param val
   * @param amount
   */
  setDefaultOpacity(val, amount) {
    return val?.indexOf(":") >= 0 ? val : val + ":" + amount;
  }

  getColorValue(val, theme) {
    const parts = val.split(":");
    let color = parts[0];

    if (parts[0].startsWith("colors")) {
      color = theme.getIn(["colors", parts[0].split("-")[1]], "#000000");
    }

    if (parts.length > 1) {
      color = onecolor(color)
        .alpha(parseInt(parts[1]) / 100)
        .cssa();
    }

    return color;
  }

  /**
   * Takes in element props set within the properties panel and returns valid linear gradient background style.
   * @param {Map} elemProps just pass in scoped elemProps
   * @param {Map}theme
   * @returns {Object} returns an object with appropriate css property or an empty object
   */
  getGradientStyle = (element, theme) => {
    const gc1 = element.get(AppConstants.BG_PROPERTY_GRADIENT_COLOR_1, element.get("gc1"));
    const gc2 = element.get(AppConstants.BG_PROPERTY_GRADIENT_COLOR_2, element.get("gc2"));
    const gr = element.get(AppConstants.BG_PROPERTY_GRADIENT_ROTATION, element.get("gr", 0));

    const getGradientColor = (color) => (color ? this.getColorValue(color, theme) : "transparent");
    return {
      backgroundImage: `linear-gradient(${gr}deg, ${getGradientColor(gc2)} 0%, ${getGradientColor(
        gc1
      )} 100%)`,
    };
  };

  /**
   * Takes in style props set within the properties panel and returns valid shadow react style. Will return empty object if no shadow settings are not found or are set to "none".  Pass in the containing element's elemProps, theme (to get theme colors), and appropriate parentType. Just merge this with the container's style.
   * @param {Map} elemProps just pass in scoped elemProps
   * @param {Map}theme
   * @param {string} parentType default | text | svg
   * @returns {Object} returns an object with appropriate css property or an empty object
   */
  getShadowStyle(elemProps, theme, parentType = "base", unset = false) {
    const defaults = AppConstants.DEFAULT_DROP_SHADOW_PROPERTIES;
    const baseData = parentType !== "button" ? { style: "none" } : {};
    const shadowProps = elemProps.get("shadow", Map(baseData)).toObject();

    const unsetBoxShadow = () => ({
      boxShadow: "none",
    });

    const unsetters = {
      button: unsetBoxShadow,
    };

    if (shadowProps?.style === "none" && unset && parentType in unsetters) {
      return unsetters[parentType]();
    }

    if (!shadowProps?.style || shadowProps?.style === "none") {
      return {};
    }

    const { style, sc, sx, sy, sb } = {
      ...defaults,
      ...shadowProps,
    };

    const shadowString = `${sx}px ${sy}px ${sb}px ${this.getColorValue(sc, theme)}`;

    const generateTransparencyShadow = () => ({ filter: `drop-shadow(${shadowString})` });
    const generateCSSBoxShadow = () => ({
      ["box-shadow"]: `${style === "inner" ? "inset " : ""}${shadowString}`,
    });
    const generateTextShadow = () => ({
      textShadow: shadowString,
    });
    const generateBoxShadow = () => ({
      boxShadow: `${style === "inner" ? "inset " : ""}${shadowString}`,
    });
    const generatePNGShadow = () =>
      style === "inner" ? generateBoxShadow() : generateTransparencyShadow();

    return {
      gallery: generateTransparencyShadow,
      png: generatePNGShadow,
      svg: generateTransparencyShadow,
      text: generateTextShadow,
      button: generateCSSBoxShadow,
      base: generateBoxShadow,
    }[parentType]();
  }

  /**
   * Generates CSS border styles from border properties and theme
   * @param {Immutable.Map} borderProps - Map containing border properties (color, width, style, radius)
   * @param {Immutable.Map} theme - Theme object containing color values
   * @returns {Object} Object containing CSS border properties
   */
  getBorderStyle(borderProps, theme) {
    const style = {};
    const formatPropMap = PropertyMaps.SHOWIT_TO_CSS_PROP_MAP;
    if (Immutable.Map.isMap(borderProps)) {
      const borderColor = borderProps.getIn(BUTTON_BORDER_COLOR.split("."));
      const borderWidth = borderProps.getIn(BUTTON_BORDER_WIDTH.split("."));
      const borderStyleVal = borderProps.getIn(BUTTON_BORDER_STYLE.split("."));
      const borderRadius = borderProps.getIn(BUTTON_BORDER_RADIUS.split("."));

      if (borderColor !== undefined && formatPropMap.bC) {
        style[formatPropMap.bC] = this.getColorValue(borderColor, theme);
      }
      if (borderWidth !== undefined && formatPropMap.bW) {
        style[formatPropMap.bW] = this._formatValue("bW", borderWidth);
      }
      if (borderStyleVal !== undefined && formatPropMap.bS) {
        style[formatPropMap.bS] = borderStyleVal;
      }
      if (borderRadius !== undefined && formatPropMap.radius) {
        if (borderRadius === BORDER_CIRCLE_RAD) {
          style[formatPropMap.radius] = borderRadius;
        } else {
          style[formatPropMap.radius] = String(borderRadius)
            .split(" ")
            .map((value) => (value === "0" ? "0" : `${value}px`))
            .join(" ");
        }
      }
    }
    return style;
  }
  
  /**
   * Takes in element props and returns the backdrop filter style object.
   * @param {Map} element - Element properties
   * @returns {Object}
   */
  getAdvancedFillStyle = (element) => {
    const backdropFilter = element.get(BACKDROP_FILTER);
    if (!hasBackdropFilter(backdropFilter)) {
      return {};
    }

    const backdropFilterCSSArray = BACKDROP_FILTER_CONFIGS.map(config => {
      const { filterFunctionName, units = ''} = config;
      const filter = getAdvanceFilterValueFromArray(backdropFilter, filterFunctionName);
      return filter !== null ? `${filterFunctionName}(${filter}${units})` : null;
    }).filter(Boolean);
    const backdropFilterCSS = backdropFilterCSSArray.length > 0 ? backdropFilterCSSArray.join(" ") : "none";

    return {
      WebkitBackdropFilter: backdropFilterCSS,
      backdropFilter: backdropFilterCSS,
    };
  }

  /**
   * Takes in elem props and returns true if a border radius above 0 is found.
   * @param {Map} elemProps just pass in scoped elemProps
   * @returns {boolean} returns true if border radius is found in elemProps
   */
  hasBorderRadius = (elemProps) => {
    const border = elemProps.get("border", false);
    if (border) {
      const radius = border.get("rad", false);
      if (
        radius === BORDER_CIRCLE_RAD ||
        String(radius)
          .split(" ")
          .reduce((hasRad, current) => {
            return Number(current) > 0 ? true : hasRad;
          }, false)
      ) {
        return true;
      }
    }
    return false;
  };

  /**
   * @param propName The Showit data modal propName
   * @param value
   * @returns {*}
   * @private
   */
  _formatValue(propName, value) {
    var defaultUnit = _.get(PropertyMaps.SHOWIT_PROPS_DEFAULT_UNITS, propName, "");

    if (defaultUnit != "") {
      if (value && _.isString(value)) {
        if (value.search(PropertyMaps.CSS_UNITS_REG_EX) == -1) {
          return value + "" + defaultUnit;
        } else {
          return value;
        }
      } else {
        return value + "" + defaultUnit;
      }
    } else {
      return value;
    }
  }

  getStyleClass(name) {
    return this.styleClass(name);
  }

  /**
   * Takes an object that contains css key value pairs and converts it to a string to be used inside a style tag
   */
  getStyleString(cssObj) {
    var css = "{";
    _.forEach(cssObj, function (value, key) {
      css += key + ": " + value + ";";
    });
    css += "}";

    return css;
  }

  /**
   * Generate a string of css using the path and a style object produced from the getStyle method
   * @param idClassPath
   * @param style
   */
  getStyleCSS(/** String **/ idClassPath, /** Object **/ style) {
    return idClassPath + " " + this.getStyleString(style);
  }
}

export default new StyleUtils();
export const FORMAT_CSS = "css";
export const FORMAT_REACT = "react";
